import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";

import { fetchMe, type Me } from "@/api/me";
import { isApiError } from "@/api/errors";
import { localizedField, localizedName } from "@/lib/localizedField";
import { Drawer } from "./Drawer";
import { LanguageToggle } from "./LanguageToggle";
import { UnitToggle } from "./UnitToggle";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function SettingsDrawer({ open, onClose }: Props): ReactNode {
  const { t, i18n } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !accessToken) return;
    let cancelled = false;
    setError(null);
    fetchMe()
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isApiError(err)) setError(err.problem.detail ?? err.problem.title);
        else if (err instanceof Error) setError(err.message);
        else setError(tc("errors.generic"));
      });
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, tc]);

  return (
    <Drawer open={open} onClose={onClose} title={tc("shell.settingsTitle")}>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ap-muted">
          {tc("shell.preferences")}
        </h3>
        <div className="flex flex-col gap-3">
          <div>
            <span className="mb-1 block text-xs text-ap-muted">{tc("shell.languageToggle")}</span>
            <LanguageToggle />
          </div>
          <div>
            <span className="mb-1 block text-xs text-ap-muted">{tc("shell.unitToggle")}</span>
            <UnitToggle />
          </div>
        </div>
        {/* The two toggles above are this browser only. Which alerts actually
            reach you, and in which language, is stored on your account — and
            that lives on its own page because the footer of every
            notification email links to it. */}
        <Link
          to="/account/notifications"
          onClick={onClose}
          className="inline-block text-sm text-ap-primary underline underline-offset-2 hover:no-underline"
        >
          {tc("shell.notificationSettings")}
        </Link>
      </section>

      <hr className="my-5 border-ap-line" />

      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ap-muted">
          {t("me.heading")}
        </h3>
        {error ? (
          <p role="alert" className="text-sm text-ap-crit">
            {error}
          </p>
        ) : !me ? (
          <p role="status" className="text-sm text-ap-muted">
            {tc("actions.loading")}
          </p>
        ) : (
          <ProfileBody me={me} lang={i18n.resolvedLanguage} />
        )}
      </section>
    </Drawer>
  );
}

function ProfileBody({ me, lang }: { me: Me; lang: string | undefined }): ReactNode {
  const { t } = useTranslation("auth");
  const lastLoginText = me.last_login_at
    ? new Date(me.last_login_at).toLocaleString(lang)
    : t("me.neverLoggedIn");

  return (
    <div className="space-y-4 text-sm">
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Row label={t("me.fullName")} value={localizedName(lang, me.full_name, me.full_name_ar)} />
        <Row label={t("me.email")} value={me.email} />
        <Row label={t("me.phone")} value={me.phone ?? "—"} />
        <Row label={t("me.status")} value={me.status} />
        <Row label={t("me.lastLogin")} value={lastLoginText} />
      </dl>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ap-muted">
          {t("me.preferences")}
        </h4>
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Row label={t("me.language")} value={me.preferences.language} />
          <Row label={t("me.unitSystem")} value={me.preferences.unit_system} />
          <Row label={t("me.timezone")} value={me.preferences.timezone} />
        </dl>
      </div>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ap-muted">
          {t("me.platformRoles")}
        </h4>
        {me.platform_roles.length === 0 ? (
          <p className="text-ap-muted">—</p>
        ) : (
          <ul className="list-inside list-disc text-ap-ink">
            {me.platform_roles.map((r) => (
              <li key={r.role}>{r.role}</li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ap-muted">
          {t("me.tenantMemberships")}
        </h4>
        {me.tenant_memberships.length === 0 ? (
          <p className="text-ap-muted">{t("me.noMemberships")}</p>
        ) : (
          <ul className="space-y-2">
            {me.tenant_memberships.map((m) => (
              <li key={m.tenant_id} className="rounded-md border border-ap-line p-2">
                <p className="font-medium text-ap-ink">
                  {localizedField(lang, m.tenant_name, m.tenant_name_ar)}{" "}
                  <span className="text-xs text-ap-muted">({m.tenant_slug})</span>
                </p>
                <p className="text-xs text-ap-muted">
                  {t("me.status")}: {m.status}
                </p>
                {m.tenant_roles.length > 0 ? (
                  <p className="text-xs text-ap-ink">
                    {t("me.tenantRoleHeader")}: {m.tenant_roles.map((r) => r.role).join(", ")}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ap-muted">
          {t("me.farmScopes")}
        </h4>
        {me.farm_scopes.length === 0 ? (
          <p className="text-ap-muted">{t("me.noFarmScopes")}</p>
        ) : (
          <ul className="list-inside list-disc text-ap-ink">
            {me.farm_scopes.map((s) => (
              <li key={s.farm_id}>
                {s.farm_id} — {s.role}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ap-muted">{label}</dt>
      <dd className="text-ap-ink">{value}</dd>
    </div>
  );
}
